import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Icon, Input } from '@shopflo/ui'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { useLocale } from 'src/lib/hooks/useLocal'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { errorToast } from 'src/lib/utils/toasters'
import useReCaptcha from 'src/lib/hooks/useReCaptcha'
import {
  resetPasswordFormSchema,
  ResetPasswordFormValues,
  defaultResetPasswordFormValues
} from 'src/@core/schemas/reset-password-schema'
import { ArrowLeft } from '@phosphor-icons/react'

const ResetPassword = () => {
  const { t } = useLocale()
  const router = useRouter()
  const { generateReCaptchaToken } = useReCaptcha()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { token } = router.query

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: defaultResetPasswordFormValues
  })

  useEffect(() => {
    if (token) {
      router.push({
        pathname: '/auth/set-password',
        query: { token }
      })
    }
  }, [token])

  const handleResetPassword = async (data: ResetPasswordFormValues) => {
    try {
      setIsLoading(true)
      const payload = {
        email: data.email.trim(),
        captcha_token: ''
      }
      await postRequest('/merchant-user/forgot-password', apiURI.AUTH_ENGINE, payload)
      router.push({
        pathname: '/auth/email-sent',
        query: { email: data.email }
      })
    } catch (e: any) {
      errorToast(e?.message || t('something_went_wrong'))
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = (data: ResetPasswordFormValues) => {
    handleResetPassword(data)
  }

  return (
    <div className='flex flex-col gap-12 max-w-[380px]'>
      <div className='flex flex-col gap-8'>
        <h2 className='text-2xl font-semibold text-zinc-700 items-center text-center'>{t('reset_password')}</h2>
        <p className='text-text-xs-regular text-zinc-500 text-center'>
          {t('pre_onboarding.reset_password_description')}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-8 min-w-[380px]'>
          <div className='flex flex-col gap-8'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-text-xs-regular text-zinc-500'>{t('email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant='filled'
                      dimension='lg'
                      className=''
                      placeholder={t('email_placeholder')}
                      error={!!form.formState.errors.email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type='submit'
              variant='primary'
              dimension='lg'
              className='w-full h-11'
              disabled={isLoading}
              loading={isLoading}
            >
              {t('reset_password')}
            </Button>
          </div>
        </form>
      </Form>

      <Button
        variant='ghost'
        dimension='lg'
        className='w-fit self-center'
        onClick={() => router.push('/auth/login')}
        leftIcon={ArrowLeft}
      >
        <span className='text-text-sm-semibold'>{t('go_back')}</span>
      </Button>
    </div>
  )
}

export default ResetPassword
